import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useGlobalState } from "../contexts/GlobalStateContext";
import { formatDateToRelativeOrShortDate } from "../Helpers/utils";
import SimpleConfirmation from "./SimpleConfirmation"; // or your modal/dialog
import Post from "./Post"; // used for embedded post previews
import useScrollLockOnMount from "../Helpers/utils";

export default function RepliesModal({ uuid, parentCommentID, onClose }) {
  const { globalState, logOut, resetGlobalState , setSessionExpired, setShowLogins} = useGlobalState();
  const [parentComment, setParentComment] = useState(null);
  const [replies, setReplies] = useState([]);
  const [hasMoreReplies, setHasMoreReplies] = useState(true);
  const [loading, setLoading] = useState(false);
  const [embeddedPosts, setEmbeddedPosts] = useState({});
  const [newReply, setNewReply] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [replyToDelete, setReplyToDelete] = useState(null);

  const scrollRef = useRef(null);

    const profileImage = globalState.isAuthenticated
    ? `${globalState.user.user_id}.jpg`
    : "default.jpg";

  const extractPostUUID = (content) => {
    const match = content.match(
      /https:\/\/textalk\.city\/post\?v=p&p=([a-z0-9-]+)/i
    );
    return match ? match[1] : null;
  };

  const fetchReplies = async () => {
    if (!hasMoreReplies || loading) return;
    setLoading(true);

    const after =
      replies.length > 0 ? replies[replies.length - 1].comment_id : null;
    const params = new URLSearchParams({ comment_id: parentCommentID });
    if (after) params.append("after_reply_id", after);

    try {
      const response = await fetch(`/php/CommentAndReplies.php?${params}`);
      const data = await response.json();

      if (data.success) {
        if (data.parent_comment && !parentComment) {
          setParentComment(data.parent_comment);
        }

        setReplies((prev) => [...prev, ...data.replies]);
        setHasMoreReplies(data.has_more_replies);
      } else {
        console.error(data.message);
      }
    } catch (err) {
      console.error("Error fetching replies:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReplies();
  }, []);

  useEffect(() => {
    const loadEmbeddedPosts = async () => {
      const newEmbeds = {};
      const allComments = [parentComment, ...replies];

      for (const comment of allComments) {
        if (!comment) continue;
        const uuid = extractPostUUID(comment.content);
        if (uuid && !embeddedPosts[uuid]) {
          try {
            const response = await fetch(`/php/fetch_post.php`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ uuid }),
            });
            const data = await response.json();
            if (data.success) {
              newEmbeds[uuid] = <Post key={uuid} post={data.post} />;
            }
          } catch (err) {
            console.error("Error fetching embedded post:", err);
          }
        }
      }

      if (Object.keys(newEmbeds).length > 0) {
        setEmbeddedPosts((prev) => ({ ...prev, ...newEmbeds }));
      }
    };

    loadEmbeddedPosts();
  }, [parentComment, replies]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 150) {
      fetchReplies();
    }
  };

  const handleDeleteClick = (commentId) => {
    setCommentToDelete(commentId);
    setReplyToDelete(null);
    setShowConfirmDialog(true);
  };

  const handleDeleteReplyClick = (parentId, replyId) => {
    setCommentToDelete(parentId);
    setReplyToDelete(replyId);
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    const targetId = replyToDelete || commentToDelete;
    const originalReplies = [...replies];
    if (!targetId) return;

    // Optimistic UI update
    if (replyToDelete) {
      setReplies((prev) => prev.filter((r) => r.comment_id !== replyToDelete));
    } else {
      setParentComment(null);
    }

    setShowConfirmDialog(false);

    try {
      const res = await fetch(`/php/delete_comment.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comment_id: targetId, uuid }),
      });

      const data = await res.json();

      if (!data.success) {
        alert(`Delete failed: ${data.message}`);
        setReplies(originalReplies);
        if (data.message === "User not authorized to delete this comment.") {
          logOut();
          resetGlobalState();
        }
      }
    } catch (err) {
      console.error("Delete error:", err);
      setReplies(originalReplies);
    } finally {
      setCommentToDelete(null);
      setReplyToDelete(null);
    }
  };

  const postReply = async () => {
    if (!globalState.isAuthenticated) {
      setShowLogins(true);
      return;
    }

    const content = newReply.trim();
    if (!content) return;

    const currentTimestamp = new Date().toISOString();
    const tempReplyId = `temp-reply-${Date.now()}`;

    // Optimistic UI Update
    const optimisticReply = {
      comment_id: tempReplyId,
      parent_comment_id: parentComment.comment_id,
      user_id: globalState.user.user_id,
      username: globalState.user.username,
      fullName: globalState.user.fullName,
      content: content,
      created_at: currentTimestamp,
    };

    setReplies((prevReplies) => [optimisticReply, ...prevReplies]);
    setNewReply(""); // Clear input

    try {
      const response = await fetch(`/php/reply_comment.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          comment_id: parentComment.comment_id,
          uuid,
          content,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          setSessionExpired(true);
          return;
        }
        throw new Error(data.message || "Unknown error");
      }

      if (data.status === "success" && data.reply) {
        // Replace temporary optimistic reply with real one
        setReplies((prevReplies) =>
          prevReplies.map((r) =>
            r.comment_id === tempReplyId ? data.reply : r
          )
        );
      } else {
        throw new Error(data.message || "Unknown error");
      }
    } catch (err) {
      console.error("Reply failed:", err);

      // Rollback optimistic reply
      setReplies((prevReplies) =>
        prevReplies.filter((r) => r.comment_id !== tempReplyId)
      );
      alert("Failed to post reply: " + err.message);
    }
  };

  const isMobile =
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  useScrollLockOnMount();

  const renderComment = (comment, isReply = false) => {
    const embedded = extractPostUUID(comment.content);
    const embeddedPost = embeddedPosts[embedded];

    return (
      <div
        key={comment.comment_id}
        className={`comment-container ${
          isReply ? "reply-container" : "comment-item"
        }`}
      >
        {/* Profile Icon */}
        <div className="profile-container">
          <Link to={`/@${encodeURIComponent(comment.username)}`}>
            <img
              src={`/profile_pics_icons/${comment.user_id}.jpg`}
              alt={`${comment.fullName}'s profile`}
              className="profile_icon"
              width={isReply ? 30 : 33}
              height={isReply ? 30 : 33}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/profile_pics_icons/default.jpg";
              }}
            />
          </Link>
        </div>

        {/* Comment Details */}
        <div className="comment-details">
          <div className="comment-header">
            <Link
              to={`/@${encodeURIComponent(comment.username)}`}
              className="comment-author"
            >
              {comment.fullName}
            </Link>
            <div className="time-delete-container">
              <span className="comment-time">
                {formatDateToRelativeOrShortDate(comment.created_at)}
              </span>
              {globalState.isAuthenticated &&
                comment.user_id === globalState.user.user_id && (
                  <button
                    className="delete-button"
                    onClick={() =>
                      isReply
                        ? handleDeleteReplyClick(
                            parentComment.comment_id,
                            comment.comment_id
                          )
                        : handleDeleteClick(comment.comment_id)
                    }
                  >
                    🗑️
                  </button>
                )}
            </div>
          </div>

          {/* Embedded post or content */}
          {embeddedPost ? (
            <div className="embedded-post-container">{embeddedPost}</div>
          ) : (
            <p className="comment-content">{comment.content}</p>
          )}
          {!isReply && (
            <div className="replies-count">
              {parseInt(comment.noOfReplies) || 0}{" "}
              {parseInt(comment.noOfReplies) === 1 ? "reply" : "replies"}
            </div>
          )}
        </div>
      </div>
    );
  };

  return createPortal(
    <>
      {showConfirmDialog && (
        <SimpleConfirmation
          message="Are you sure you want to delete this comment?"
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowConfirmDialog(false);
            setCommentToDelete(null);
            setReplyToDelete(null);
          }}
        />
      )}

      <div className="comments-modal">
        <div id="comments-modal" style={{ display: "block" }}>
          <div
            className={
              isMobile ? "messages-content-mobile" : "messages-content"
            }
          >
            {/* Header */}
            <div id="messages-header">
              <span className="comments-title">Replies:</span>
              <span className="close" onClick={onClose}>
                &times;
              </span>
            </div>

            {/* Comments Section */}
            <div
              id="comments-section"
              onScroll={handleScroll}
              ref={scrollRef}
              style={{ overflowY: "auto" }}
            >
              {loading && replies.length === 0 ? (
                <p>Loading...</p>
              ) : !parentComment ? (
                <p>Comment not found or deleted.</p>
              ) : (
                <>
                  {renderComment(parentComment)}
                  {replies.map((reply) => renderComment(reply, true))}
                  {loading && <p>Loading more replies...</p>}
                  {!hasMoreReplies && <p>No more replies.</p>}
                </>
              )}
            </div>

            {/* Footer for reply input */}
            <div className="comments-footer">
              <img
                className="profile_icon"
                id="commenter_icon"
                src={`/profile_pics_icons/${profileImage}`}
                height="42"
                width="42"
                alt="Your profile picture"
                loading="lazy"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/profile_pics_icons/default.jpg";
                }}
              />
              <textarea
                className="fullTextbox"
                id="shortComment"
                placeholder="Type your comment here..."
                value={newReply}
                rows="1"
                aria-label="Write a reply"
                onChange={(e) => setNewReply(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    postReply();
                  }
                }}
              />
              <button
                id="postShortComment"
                className="save"
                aria-label="Post comment"
                onClick={postReply}
                disabled={!newReply.trim()}
              >
                Post
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
